import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import DataMap from '../../components/DataMap/DataMap'
import useCustomerList from '../../utils/useCustomerList'
import { settledList, loadErrorMessage } from '../../utils/loadState'
import { pollRebuild, rebuildMessage } from '../../utils/rebuildPoll'
import {
  getSchemas,
  buildContextGraph, getContextGraphStatus, getContextGraphRelationships, getContextGraphJoinPath,
  getContextGraphMap,
} from '../../api'
import './ContextGraph.css'

const REBUILD_POLL_MS = 5000
const REBUILD_POLL_LIMIT = 360 // 30 minutes

export default function ContextGraph() {
  const { customers, loading, error: customersError, reload: reloadCustomers } = useCustomerList(list => {
    if (list.length > 0) setSelectedCustomerId(list[0].customer_id || list[0].id)
  })
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [schemas, setSchemas] = useState(null)
  const [status, setStatus] = useState(null)
  const [relationships, setRelationships] = useState([])
  const [graphError, setGraphError] = useState('')
  // The customer whose Rebuild this page is polling; the button reflects only the
  // customer selected now.
  const [rebuildingFor, setRebuildingFor] = useState(null)
  const rebuilding = rebuildingFor !== null && rebuildingFor === selectedCustomerId
  const [fromTable, setFromTable] = useState('')
  const [toTable, setToTable] = useState('')
  const [pathResult, setPathResult] = useState(null)
  const [pathLoading, setPathLoading] = useState(false)
  const [selectedTable, setSelectedTable] = useState(null)

  // Each read is settled on its own (item 213): a table list that could not be read (503)
  // must not blank the graph status and relationships that did load. What failed is named
  // with a Retry, and never counted as zero.
  const [loadErrors, setLoadErrors] = useState({})
  // The data map: its own read, so a failure names itself beside the map and never blanks
  // the rest of the page.
  const [map, setMap] = useState({ data: null, error: null, loading: false })

  // Each load holds a token; a load that finishes after a newer one started (a customer
  // switch, a retry) sets nothing, so one customer's map never lands under another's name.
  const loadRun = useRef(0)
  const loadGraph = useCallback(async (customerId) => {
    if (!customerId) return
    const token = ++loadRun.current
    setGraphError('')
    setMap({ data: null, error: null, loading: true })
    const [schemaRes, statusRes, relRes, mapRes] = await Promise.allSettled([
      getSchemas(customerId),
      getContextGraphStatus(customerId),
      getContextGraphRelationships(customerId),
      getContextGraphMap(customerId),
    ])
    if (loadRun.current !== token) return
    setMap(mapRes.status === 'fulfilled'
      ? { data: mapRes.value, error: null, loading: false }
      : { data: null, error: mapRes.reason, loading: false })
    const errors = {}

    const tl = settledList(schemaRes, 'tables', 'schema')
    if (tl.error) errors.tables = tl.error
    const tables = tl.items || []
    const nameOf = (t) => (t ? (t.name || t.table_name || '') : '')
    setSchemas(tl.items ? { tables } : null)
    setFromTable(nameOf(tables[0]))
    setToTable(nameOf(tables.length > 1 ? tables[1] : tables[0]))
    setSelectedTable(tables[0] || null)

    if (statusRes.status === 'fulfilled') {
      setStatus(statusRes.value)
    } else {
      setStatus(null)
      errors.status = loadErrorMessage(statusRes.reason)
    }

    const rl = settledList(relRes, 'relationships')
    if (rl.error) errors.relationships = rl.error
    setRelationships(rl.items || [])
    // the graph answered and reported its own problem: shown in the banner, as before
    if (relRes.status === 'fulfilled' && relRes.value?.error) setGraphError(relRes.value.error)

    if (Object.keys(errors).length) console.error('Context Graph reads that failed:', errors)
    setLoadErrors(errors)
  }, [])

  useEffect(() => {
    loadGraph(selectedCustomerId)
  }, [selectedCustomerId, loadGraph])

  const retryGraph = () => loadGraph(selectedCustomerId)

  // A customer switch or leaving the page cancels a Rebuild poll in flight: each poll
  // holds a token, and the cleanup below moves the counter past it.
  const rebuildRun = useRef(0)
  const selectedRef = useRef(selectedCustomerId)
  useEffect(() => {
    selectedRef.current = selectedCustomerId
    const run = rebuildRun
    return () => { run.current += 1 }
  }, [selectedCustomerId])

  // The rebuild runs in the background on the server and the current graph keeps
  // serving until the new one is swapped in, so the page polls the status until the
  // rebuild reports done (utils/rebuildPoll.js), then reloads.
  async function handleRebuild() {
    if (!selectedCustomerId || rebuilding) return
    const customerId = selectedCustomerId
    const token = ++rebuildRun.current
    const stillWanted = () => rebuildRun.current === token && selectedRef.current === customerId
    setRebuildingFor(customerId)
    try {
      const res = await buildContextGraph(customerId, true)
      if (!stillWanted()) return
      if (res && res.success === false) throw new Error(res.error || 'The rebuild was not scheduled')
      const result = await pollRebuild({
        getStatus: () => getContextGraphStatus(customerId),
        isCurrent: stillWanted,
        sleep: () => new Promise(resolve => setTimeout(resolve, REBUILD_POLL_MS)),
        limit: REBUILD_POLL_LIMIT,
      })
      if (result.cancelled) return
      await loadGraph(customerId)
      if (!stillWanted()) return
      const message = rebuildMessage(result)
      if (message) setGraphError(message)
    } catch (err) {
      if (!stillWanted()) return
      console.error('Rebuild failed:', err)
      setGraphError(err.message || 'Rebuild failed')
    } finally {
      setRebuildingFor(cur => (cur === customerId ? null : cur))
    }
  }

  const tables = schemas?.tables || schemas?.schema || []
  const tableNames = Array.isArray(tables) ? tables.map(t => t.name || t.table_name || t) : []

  // Verified = seeded from the vendor schema or declared (a flattened child table's link to
  // its parent, declared:parent_child); everything else was found by matching values
  // (value_match and its value_match:<rule> variants).
  const VERIFIED_METHODS = new Set(['explicit', 'seeded', 'declared:parent_child'])
  const isVerified = r => VERIFIED_METHODS.has(r.detection_method)
  const fkCount = relationships.filter(isVerified).length
  const inferredCount = relationships.filter(r => r.detection_method && !isVerified(r)).length

  // Table -> set of tables it's related to, from the REAL context graph, not guessed from schema columns.
  const joinsByTable = {}
  relationships.forEach(r => {
    const from = r.from_table
    const to = r.to_table
    if (!from || !to) return
    if (!joinsByTable[from]) joinsByTable[from] = []
    joinsByTable[from].push(`${to}.${r.to_column || ''}`)
    if (!joinsByTable[to]) joinsByTable[to] = []
    joinsByTable[to].push(`${from}.${r.from_column || ''}`)
  })

  async function findPath() {
    if (!fromTable || !toTable || !selectedCustomerId) return
    if (fromTable === toTable) {
      setPathResult({ found: false, message: 'Same table selected' })
      return
    }
    setPathLoading(true)
    setPathResult(null)
    try {
      const res = await getContextGraphJoinPath(selectedCustomerId, fromTable, toTable)
      if (res.error) {
        setPathResult({ found: false, message: res.error })
      } else {
        setPathResult({
          found: true,
          hops: res.hop_count,
          joins: res.joins,
          sql: res.sql,
          confidence: res.confidence,
        })
      }
    } catch (err) {
      setPathResult({ found: false, message: err.message || 'Join path lookup failed' })
    } finally {
      setPathLoading(false)
    }
  }

  const displayTables = tables
  const displayTableNames = tableNames

  const currentTable = selectedTable || displayTables[0]
  const currentColumns = currentTable?.columns || []
  const currentTableName = currentTable?.name || currentTable?.table_name || ''

  const selectedCustomer = customers.find(c => (c.customer_id || c.id) === selectedCustomerId)

  // a table clicked on the map opens in Table Detail (the map row carries its physical name)
  const showTableDetail = (row) => {
    const names = [row?.physical, row?.id].filter(Boolean)
    const hit = displayTables.find(t => names.includes(t.name || t.table_name))
      || displayTables.find(t => (t.name || t.table_name || '').endsWith(`_${row?.id}`))
    if (hit) setSelectedTable(hit)
  }

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Context Graph' },
  ]
  const navActions = (
    <>
      <Link to="/admin" className="nav-btn">← Dashboard</Link>
    </>
  )

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
        <div className="cg-loading"><div className="cg-spinner" /><p>Loading context graph…</p></div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="cg-page">
        {/* Header */}
        <div className="cg-header">
          <div>
            <h1 className="cg-title">Context Graph</h1>
            <p className="cg-subtitle">Table relationships and join paths for customer data</p>
          </div>
          <div className="cg-header-actions">
            <select className="cg-select" aria-label="Customer" value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)}>
              <option value="" disabled>Select a customer</option>
              {customers.map(c => (
                <option key={c.customer_id || c.id} value={c.customer_id || c.id}>{c.company_name || c.name || c.customer_id}</option>
              ))}
            </select>
            <button className="nav-btn primary" style={{ padding: '8px 16px' }} onClick={handleRebuild} disabled={rebuilding || !selectedCustomerId}>
              {rebuilding ? 'Rebuilding…' : 'Rebuild Graph'}
            </button>
          </div>
        </div>

        {customersError && <LoadError what="customers" error={customersError} onRetry={reloadCustomers} />}
        {graphError && (
          <div className="cg-error-banner">{graphError}</div>
        )}
        {loadErrors.tables && (
          <LoadError what="the table list" error={loadErrors.tables} onRetry={retryGraph} />
        )}
        {loadErrors.status && (
          <LoadError what="the graph status" error={loadErrors.status} onRetry={retryGraph} />
        )}
        {loadErrors.relationships && (
          <LoadError what="the relationships" error={loadErrors.relationships} onRetry={retryGraph} />
        )}

        {/* The menu link carries no customer, so the page asks for one instead of
            showing an empty graph with zero counts. */}
        {!selectedCustomerId ? (
          <div className="cg-empty-note">
            {customers.length === 0 && !customersError
              ? 'No customers yet. Add one on the Customers page to build its Context Graph.'
              : 'Select a customer above to see how its tables connect and join.'}
          </div>
        ) : (
          <>
          {/* Stats — from the real Context Graph engine (routers/admin.py's /context-graph/{id}/status), not placeholders.
              Without the status they fall back to the loaded lists; a list that failed shows a dash, not a zero. */}
          <div className="cg-stat-grid">
            <div className="cg-stat-card">
              <div className="cg-stat-label">Tables (Nodes)</div>
              <div className="cg-stat-value">{status?.exists ? status.tables : (loadErrors.tables ? '—' : displayTables.length)}</div>
            </div>
            <div className="cg-stat-card">
              <div className="cg-stat-label">Relationships (Edges)</div>
              <div className="cg-stat-value">{status?.exists ? status.relationships : (loadErrors.relationships ? '—' : relationships.length)}</div>
            </div>
            <div className="cg-stat-card">
              <div className="cg-stat-label">Explicit/Seeded</div>
              <div className="cg-stat-value green">{loadErrors.relationships ? '—' : fkCount}</div>
            </div>
            <div className="cg-stat-card">
              <div className="cg-stat-label">Inferred (value-overlap)</div>
              <div className="cg-stat-value blue">{loadErrors.relationships ? '—' : inferredCount}</div>
            </div>
          </div>

          {status?.exists && (
            <div className="cg-built-note" style={{ fontSize: 13, opacity: 0.75, margin: '4px 0 12px' }}>
              {status.last_built
                ? `Last built ${new Date(status.last_built).toLocaleString()}`
                : 'Build time not recorded'}
              {status.rebuild?.running ? ' · a rebuild is running; this graph stays in use until it finishes' : ''}
            </div>
          )}

          {status?.exists === false && (
            <div className="cg-empty-note">
              No Context Graph has been built yet for this customer. Click "Rebuild Graph" to build one from the live schema.
            </div>
          )}

          {/* The data map: every table on a ring by area, the keys they share, verified joins */}
          <div className="cg-map">
            <DataMap data={map.data} loading={map.loading} error={map.error} onRetry={retryGraph}
              audience="admin" subject={selectedCustomer?.company_name || selectedCustomer?.name || selectedCustomerId}
              onSelectTable={showTableDetail} />
          </div>

          {/* Path Finder — real join-path lookup against the Context Graph engine */}
          <div className="cg-path-finder">
            <div className="cg-subsection-title">Join Path Finder</div>
            <div className="cg-path-row">
              <div className="cg-form-group">
                <label className="cg-form-label">From Table</label>
                <select className="cg-select" value={fromTable} onChange={e => setFromTable(e.target.value)}>
                  {displayTableNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <span className="cg-path-arrow">→</span>
              <div className="cg-form-group">
                <label className="cg-form-label">To Table</label>
                <select className="cg-select" value={toTable} onChange={e => setToTable(e.target.value)}>
                  {displayTableNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <button className="cg-find-btn" onClick={findPath} disabled={pathLoading}>
                {pathLoading ? 'Finding…' : 'Find Path'}
              </button>
            </div>
            {pathResult && (
              <div className="cg-path-result">
                {pathResult.found ? (
                  <>
                    <strong>Path found ({pathResult.hops} hop{pathResult.hops !== 1 ? 's' : ''}, confidence {Math.round((pathResult.confidence ?? 1) * 100)}%):</strong>
                    {pathResult.sql && <div className="cg-path-chain cg-mono">{pathResult.sql}</div>}
                  </>
                ) : (
                  <span style={{ color: 'var(--gray-500)' }}>{pathResult.message}</span>
                )}
              </div>
            )}
          </div>

          {/* Table Detail */}
          <div className="cg-table-detail">
            <div className="cg-detail-head">
              <div className="cg-subsection-title">Table Detail — {currentTableName || '—'}</div>
              {displayTables.length > 0 && (
                <select className="cg-select" aria-label="Table" value={currentTableName}
                  onChange={e => setSelectedTable(displayTables.find(t => (t.name || t.table_name) === e.target.value) || null)}>
                  {displayTableNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
            </div>
            <div className="cg-detail-table-wrap">
              <table className="cg-detail-table">
                <thead>
                  <tr>
                    <th>Column</th>
                    <th>Type</th>
                    <th>Sample Value</th>
                    <th>Nulls</th>
                    <th>Joins To (Context Graph)</th>
                  </tr>
                </thead>
                <tbody>
                  {currentColumns.map((c, i) => {
                    const colName = c.name || c.column_name
                    const joins = joinsByTable[currentTableName] || []
                    return (
                      <tr key={i}>
                        <td className="cg-mono">{colName}</td>
                        <td>{c.type || c.data_type || 'VARCHAR'}</td>
                        <td className="cg-mono cg-muted">{c.sample || c.sample_value || '—'}</td>
                        <td>{c.nulls || c.null_pct || '0%'}</td>
                        <td className={joins.length ? 'cg-join-ref' : 'cg-muted'}>{joins.length ? joins.join(', ') : '—'}</td>
                      </tr>
                    )
                  })}
                  {currentColumns.length === 0 && (
                    <tr><td colSpan={5} style={{ color: 'var(--gray-400)', textAlign: 'center', padding: 20 }}>{loadErrors.tables ? "The table list couldn't load" : 'No column data available'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}
      </div>
    </>
  )
}
